import { axiosBarwisInstance } from '../axios/axios.lib';

/**
 * Theme Service
 * Handles fetching and applying theme from API
 */

/**
 * Convert hex color to RGB
 */
const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
};

/**
 * Get contrast color (black or white) for a background color
 */
const getContrastColor = (backgroundColor) => {
  const rgb = hexToRgb(backgroundColor);
  if (!rgb) return '#000000';
  
  const { r, g, b } = rgb;
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 128 ? '#000000' : '#ffffff';
};

/**
 * Lighten a color
 */
const lightenColor = (hex, percent) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  
  const { r, g, b } = rgb;
  const newR = Math.min(255, Math.round(r + (255 - r) * percent / 100));
  const newG = Math.min(255, Math.round(g + (255 - g) * percent / 100));
  const newB = Math.min(255, Math.round(b + (255 - b) * percent / 100));
  
  return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
};

/**
 * Darken a color
 */
const darkenColor = (hex, percent) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  
  const { r, g, b } = rgb;
  const newR = Math.max(0, Math.round(r * (1 - percent / 100)));
  const newG = Math.max(0, Math.round(g * (1 - percent / 100)));
  const newB = Math.max(0, Math.round(b * (1 - percent / 100)));
  
  return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
};

/**
 * Update CSS variable
 */
const updateCSSVariable = (variable, value) => {
  document.documentElement.style.setProperty(variable, value);
};

/**
 * Apply theme colors to CSS variables
 */
const applyThemeColors = (themeData) => {
  const {
    colors_primary,
    colors_secondary,
    colors_background,
    colors_surface
  } = themeData;

  // Calculate text color based on background
  const textColor = getContrastColor(colors_background);
  
  // Calculate brightness to determine dark/light mode
  const backgroundRgb = hexToRgb(colors_background);
  const brightness = backgroundRgb 
    ? (backgroundRgb.r * 299 + backgroundRgb.g * 587 + backgroundRgb.b * 114) / 1000
    : 0;

  // Primary color and its variants
  updateCSSVariable('--ion-color-primary', colors_primary);
  const primaryRgb = hexToRgb(colors_primary);
  if (primaryRgb) {
    updateCSSVariable('--ion-color-primary-rgb', `${primaryRgb.r}, ${primaryRgb.g}, ${primaryRgb.b}`);
  }
  updateCSSVariable('--ion-color-primary-contrast', getContrastColor(colors_primary));
  const primaryContrastRgb = hexToRgb(getContrastColor(colors_primary));
  if (primaryContrastRgb) {
    updateCSSVariable('--ion-color-primary-contrast-rgb', `${primaryContrastRgb.r}, ${primaryContrastRgb.g}, ${primaryContrastRgb.b}`);
  }
  updateCSSVariable('--ion-color-primary-shade', darkenColor(colors_primary, 12));
  updateCSSVariable('--ion-color-primary-tint', lightenColor(colors_primary, 10));

  // Secondary color and its variants
  updateCSSVariable('--ion-color-secondary', colors_secondary);
  const secondaryRgb = hexToRgb(colors_secondary);
  if (secondaryRgb) {
    updateCSSVariable('--ion-color-secondary-rgb', `${secondaryRgb.r}, ${secondaryRgb.g}, ${secondaryRgb.b}`);
  }
  updateCSSVariable('--ion-color-secondary-contrast', getContrastColor(colors_secondary));
  const secondaryContrastRgb = hexToRgb(getContrastColor(colors_secondary));
  if (secondaryContrastRgb) {
    updateCSSVariable('--ion-color-secondary-contrast-rgb', `${secondaryContrastRgb.r}, ${secondaryContrastRgb.g}, ${secondaryContrastRgb.b}`);
  }
  updateCSSVariable('--ion-color-secondary-shade', darkenColor(colors_secondary, 12));
  updateCSSVariable('--ion-color-secondary-tint', lightenColor(colors_secondary, 10));

  // Background colors
  updateCSSVariable('--ion-background-color', colors_background);
  if (backgroundRgb) {
    updateCSSVariable('--ion-background-color-rgb', `${backgroundRgb.r}, ${backgroundRgb.g}, ${backgroundRgb.b}`);
  }

  // Surface color (new field)
  if (colors_surface) {
    updateCSSVariable('--ion-background-color-secondary', colors_surface);
    const surfaceRgb = hexToRgb(colors_surface);
    if (surfaceRgb) {
      updateCSSVariable('--ion-background-color-secondary-rgb', `${surfaceRgb.r}, ${surfaceRgb.g}, ${surfaceRgb.b}`);
    }
    // Generate tertiary background from surface
    const backgroundTertiary = lightenColor(colors_surface, 5);
    updateCSSVariable('--ion-background-color-tertiary', backgroundTertiary);
    const backgroundTertiaryRgb = hexToRgb(backgroundTertiary);
    if (backgroundTertiaryRgb) {
      updateCSSVariable('--ion-background-color-tertiary-rgb', `${backgroundTertiaryRgb.r}, ${backgroundTertiaryRgb.g}, ${backgroundTertiaryRgb.b}`);
    }
  } else {
    // Fallback: Generate secondary and tertiary backgrounds based on main background
    const backgroundSecondary = lightenColor(colors_background, 5);
    const backgroundTertiary = lightenColor(colors_background, 10);
    updateCSSVariable('--ion-background-color-secondary', backgroundSecondary);
    const backgroundSecondaryRgb = hexToRgb(backgroundSecondary);
    if (backgroundSecondaryRgb) {
      updateCSSVariable('--ion-background-color-secondary-rgb', `${backgroundSecondaryRgb.r}, ${backgroundSecondaryRgb.g}, ${backgroundSecondaryRgb.b}`);
    }
    updateCSSVariable('--ion-background-color-tertiary', backgroundTertiary);
    const backgroundTertiaryRgb = hexToRgb(backgroundTertiary);
    if (backgroundTertiaryRgb) {
      updateCSSVariable('--ion-background-color-tertiary-rgb', `${backgroundTertiaryRgb.r}, ${backgroundTertiaryRgb.g}, ${backgroundTertiaryRgb.b}`);
    }
  }

  // Text colors
  updateCSSVariable('--ion-text-color', textColor);
  const textRgb = hexToRgb(textColor);
  if (textRgb) {
    updateCSSVariable('--ion-text-color-rgb', `${textRgb.r}, ${textRgb.g}, ${textRgb.b}`);
  }
  updateCSSVariable('--ion-text-color-light', getContrastColor(colors_background));
  const textLightRgb = hexToRgb(getContrastColor(colors_background));
  if (textLightRgb) {
    updateCSSVariable('--ion-text-color-light-rgb', `${textLightRgb.r}, ${textLightRgb.g}, ${textLightRgb.b}`);
  }

  // Update toolbar background to match main background
  updateCSSVariable('--ion-toolbar-background', colors_background);
  updateCSSVariable('--ion-toolbar-border-color', colors_background);
  
  // Update tab bar color to match text color
  updateCSSVariable('--ion-tab-bar-color', textColor);

  // Store theme mode and logo URLs
  const isDarkMode = brightness < 128;
  const logoPath = isDarkMode ? themeData.logo_light : themeData.logo_dark;
  
  // Store logo URL as CSS variable for dynamic usage
  if (logoPath) {
    const apiUrl = process.env.REACT_APP_API_URL || '';
    const baseUrl = apiUrl.replace(/\/$/, '');
    const path = logoPath.startsWith('/') ? logoPath.substring(1) : logoPath;
    const logoUrl = `${baseUrl}/storage/${path}`;
    updateCSSVariable('--theme-logo-url', `url(${logoUrl})`);
  }

  // Store login image URL
  if (themeData.login_image) {
    const apiUrl = process.env.REACT_APP_API_URL || '';
    const baseUrl = apiUrl.replace(/\/$/, '');
    const path = themeData.login_image.startsWith('/') ? themeData.login_image.substring(1) : themeData.login_image;
    const loginImageUrl = `${baseUrl}/storage/${path}`;
    updateCSSVariable('--theme-login-image-url', `url(${loginImageUrl})`);
  }

  // Store logo icon URL
  if (themeData.logo_icon) {
    const apiUrl = process.env.REACT_APP_API_URL || '';
    const baseUrl = apiUrl.replace(/\/$/, '');
    const path = themeData.logo_icon.startsWith('/') ? themeData.logo_icon.substring(1) : themeData.logo_icon;
    const logoIconUrl = `${baseUrl}/storage/${path}`;
    updateCSSVariable('--theme-logo-icon-url', logoIconUrl);
  }
};

/**
 * Fetch theme from API and apply it
 */
export const loadTheme = async () => {
  try {
    const response = await axiosBarwisInstance.get('/api/current-theme');
    const themeData = response.data;
    
    // Apply theme colors
    applyThemeColors(themeData);
    
    // Store theme data for future use (logos, images, etc.)
    localStorage.setItem('currentTheme', JSON.stringify(themeData));
    
    return themeData;
  } catch (error) {
    console.error('Error loading theme:', error);
    // Apply default theme if API fails
    applyDefaultTheme();
    throw error;
  }
};

/**
 * Get current theme data from localStorage
 */
export const getCurrentTheme = () => {
  try {
    const themeData = localStorage.getItem('currentTheme');
    return themeData ? JSON.parse(themeData) : null;
  } catch (error) {
    console.error('Error getting theme from localStorage:', error);
    return null;
  }
};

/**
 * Apply default theme (fallback)
 */
const applyDefaultTheme = () => {
  const defaultTheme = {
    colors_primary: '#ffffff',
    colors_secondary: '#ff0000',
    colors_background: '#000000',
    colors_surface: '#121212'
  };
  applyThemeColors(defaultTheme);
};

/**
 * Get theme image URL
 */
export const getThemeImageUrl = (imagePath) => {
  if (!imagePath) return null;
  const apiUrl = process.env.REACT_APP_API_URL || '';
  // Remove trailing slash from API URL if present
  const baseUrl = apiUrl.replace(/\/$/, '');
  // Ensure image path doesn't start with slash
  const path = imagePath.startsWith('/') ? imagePath.substring(1) : imagePath;
  // Append /storage/ to the path
  return `${baseUrl}/storage/${path}`;
};

/**
 * Check if current theme is dark mode
 */
export const isDarkMode = () => {
  const theme = getCurrentTheme();
  if (!theme || !theme.colors_background) return true; // Default to dark
  
  const rgb = hexToRgb(theme.colors_background);
  if (!rgb) return true;
  
  const brightness = (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000;
  return brightness < 128;
};

/**
 * Get current logo URL based on dark/light mode
 */
export const getCurrentLogoUrl = () => {
  const theme = getCurrentTheme();
  if (!theme) return null;
  
  const darkMode = isDarkMode();
  const logoPath = darkMode ? theme.logo_light : theme.logo_dark;
  
  return getThemeImageUrl(logoPath);
};

/**
 * Get logo URL for specific mode
 */
export const getLogoUrl = (useDarkMode = null) => {
  const theme = getCurrentTheme();
  if (!theme) return null;
  
  // If useDarkMode is explicitly provided, use it; otherwise determine from theme
  const darkMode = useDarkMode !== null ? useDarkMode : isDarkMode();
  const logoPath = darkMode ? theme.logo_dark : theme.logo_light;
  
  return getThemeImageUrl(logoPath);
};

/**
 * Get login image URL
 */
export const getLoginImageUrl = () => {
  const theme = getCurrentTheme();
  if (!theme || !theme.login_image) return null;
  
  return getThemeImageUrl(theme.login_image);
};

/**
 * Get logo icon URL
 */
export const getLogoIconUrl = () => {
  const theme = getCurrentTheme();
  if (!theme || !theme.logo_icon) return null;
  
  return getThemeImageUrl(theme.logo_icon);
};

const themeService = {
  loadTheme,
  getCurrentTheme,
  getThemeImageUrl,
  isDarkMode,
  getCurrentLogoUrl,
  getLogoUrl,
  getLoginImageUrl,
  getLogoIconUrl
};

export default themeService;

